<template>
  <div class="multiple-choice">
    <div class="question-banner">
      <v-fade-transition>
        <div
        transition="scale-transition"
        :class="{correct: isAnswerCorrect, incorrect: !isAnswerCorrect}"
        v-show="showBanner && !showSolution">
          {{bannerMessage}}
        </div>
      </v-fade-transition>
    </div>
    <div class="question-container">
      <div class="question-content">
        <span v-html="questionContent.QuestionContent[0]"></span>
      </div>
      <div v-if="questionContent.SecondColumn" class="question-media">
        <span v-html="questionContent.SecondColumn[0]"></span>
      </div>
      <div class="question-choices">
        <hr>
        <div class="choices-grid">
          <div
            class="choices-item"
            v-for="(choice, i) in questionContent.MCoption"
            :key="i"
          >
            <template v-if="choice.Correct">
              <div
              class="choice-message"
              v-if="answerIndex !== '' || showSolution">
                <template v-if="i === answerIndex">
                  Your answer:
                </template>
                <template v-else>
                  Correct answer:
                </template>
              </div>
              <v-btn class="choice-group"
                :id="'choice-answer-' + i"
                @click="answer(true, i)"
                :class="{
                  correct: i === answerIndex && !showSolution,
                  'solution-correct': (i !== answerIndex && answerIndex !== '') || showSolution,
                  disabled: showSolution
                }"
              >
                <div class="choice-identifier">{{ choice.MCidentifier[0] }}</div>
                <div class="choice-content" v-html="choice.Correct[0]"></div>
                <v-icon v-show="i === answerIndex && !showSolution">
                  mdi-check-circle
                </v-icon>
                <v-icon
                  v-show="showSolution
                    || (i !== answerIndex && answerIndex !== '')">
                  mdi-check
                </v-icon>
              </v-btn>
            </template>
            <template v-else>
              <div
                class="choice-message"
                v-show="i === answerIndex && !showSolution"
              >
                Your answer:
              </div>
              <v-btn class="choice-group"
                :id="'choice-answer-' + i"
                @click="answer(false, i)"
                :class="{
                  incorrect: i === answerIndex && !showSolution,
                  disabled: showSolution
                }"
              >
                <div class="choice-identifier">
                  {{ choice.MCidentifier[0] }}
                </div>
                <div class="choice-content" v-html="choice._"></div>
                <v-icon v-show="i === answerIndex && !showSolution">
                  mdi-close-circle
                </v-icon>
              </v-btn>
            </template>
          </div>
        </div>
      </div>
      <div class="question-solution" v-show="showSolution">
        <div class="solution-content">
          <span v-html="questionContent.Solution[0]"></span>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="js">
import { mapGetters } from 'vuex'

export default {
  name: 'MultipleChoice',
  props: {
    questionContent: Object,
    bookTitle: String,
    chapter: String,
    section: String
  },
  data () {
    return {
      answerIndex: '',
      isAnswerCorrect: false,
      showBanner: false
    }
  },
  methods: {
    answer (isCorrect, choiceIndex) {
      this.answerIndex = choiceIndex
      this.isAnswerCorrect = isCorrect
      this.showBanner = true

      const gtmCorrect = isCorrect ? 'true' : 'false'
      this.$gtm.trackEvent({
        event: 'user_selected_answer',
        category: 'Assessment',
        action: 'click',
        label: 'User selected correct answer',
        value: 'correct',
        productName: this.bookTitle,
        chapterName: this.chapter,
        sectionTitle: this.section,
        isCorrect: gtmCorrect
      })

      setTimeout(() => {
        this.showBanner = false
      }, 3000)
    }
  },
  computed: {
    ...mapGetters({
      showSolution: 'question/getShowSolution'
    }),
    bannerMessage () {
      return this.isAnswerCorrect ? 'Correct' : 'Incorrect'
    },
    choiceMessage () {
      return this.showSolution ? 'Correct answer:' : 'Your answer:'
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables.scss";
.multiple-choice {
  max-width: 100%;
  .question-banner {
    position: fixed;
    left: 30px;
    top: 120px;
    width: 80%;
    height: 58px;
    left: 50%;
    transform: translate(-50%, 0);
    z-index: 1;
    div {
      margin: auto;
      text-align: center;
      line-height: 58px;
      font-size: 20px;
      font-weight: 800;
      letter-spacing: 1.25px;
      color: #fff;
      border-radius: 8px;
    }
    .correct {
      background: $question-correct-color;
    }
    .incorrect {
      background: $question-incorrect-color;
    }
  }
  .question-container {
    .question-content {
      width:50%;
      margin: 32px 0px;
      display: inline-block;
      padding-right: $column-padding;
    }
    .question-choices {
      width:50%;
      padding-right: $column-padding;
      .choices-grid {
        margin: 36px auto;
        display: grid;
        grid-template-columns: 1fr;
        grid-gap: $choices-gap;
        width: 95%;
        .choices-item {
          text-transform: none;
          .choice-message, .correct-message {
            font-size: 14px;
            line-height: 20px;
            margin-bottom: 12px;
            color: $choices-message-color;
          }
          .v-btn {
            white-space: normal;
          }
          .choice-group {
            box-sizing: border-box;
            display: flex;
            flex-direction: row wrap;
            align-items: center;
            justify-content: flex-start;
            width: 100%;
            height: auto;
            padding: 8px 12px 8px 8px;
            background-color: $question-default-background-color;
            border: 1px solid $question-default-border-color;
            border-radius: 8px;
            box-shadow: none;
            ::v-deep .v-btn__content {
              flex: auto;
            }
            .v-icon {
              margin-left: auto;
              font-size: 20px;
              transition: color 0.3s;
            }
            &:focus-visible {
              outline: 4px solid $focus-button-outline;
              outline-offset: 4px;
              &::before {
                background-color: $question-default-background-color !important;
              }
            }
            &.correct {
              background: $question-correct-background-color;
              border-color: $question-correct-color;
              .v-icon {
                color: $question-correct-color;
              }
            }
            &.solution-correct {
              border-color: $question-correct-color;
              .v-icon {
                color: $question-correct-color;
              }
            }
            &.disabled {
              pointer-events: none;
            }
            &.incorrect {
              background: $question-incorrect-background-color;
              border-color: $question-incorrect-color;
              .v-icon {
                color:  $question-incorrect-color;
              }
            }
            &:hover {
              background: $question-hover-background-color;
              border-color: $question-default-border-color;
            }
            .choice-identifier {
              white-space: nowrap;
              width: 32px;
              height: 32px;
              justify-content: center;
              border: 1px solid $question-default-border-color;
              border-radius: 16px;
              background: white;
              padding: $choices-identifier-padding;
              font-weight: 700;
              font-size: 16px;
              line-height: 16px;
              text-align: center;
              letter-spacing: 0.05em;
              margin-right: 16px;
            }
            .choice-content {
              text-align: left;
              text-transform: none !important;
              font-weight: 400px;
              font-size: 16px;
              line-height: 22px;
              margin: 8px;
              ::v-deep p {
                margin-bottom: 0px !important;
              }
            }
          }
        }
      }
    }
    .question-media {
      float: right;
      width:50%;
      padding-left: $column-padding;
    }
    .question-solution {
      width:50%;
      padding-right: $column-padding;
      .solution-content {
        background: $question-correct-background-color;
        border-radius: $solution-border-radius;
        width: 100%;
        margin: 0px 4px;
        padding: $solution-content-padding;
        ::v-deep p {
          margin-bottom: 0px !important;
        }
      }
    }
  }
@media (max-width: 768px) {
  .question-banner {
    width: 95%;
    top: 80px;
  }
  .question-container {
    hr {
      width: 100%;
      margin-top: $question-hr-margin-top;
    }
    .question-content, .question-choices, .question-solution {
      @include mobile-sizing;
    }
    .question-media {
      @include mobile-sizing;
      float: none;
    }
  }
}
}
</style>
